import { api } from '@/lib/api';
import type {
  StreamsFilters,
  StreamsResponse,
  StreamEditResponse,
  StreamNewOptionsResponse,
  StreamFormData,
  V2Stream,
  Organisation,
} from './types';

// =============================================================================
// Streams List (V4 — PostgreSQL)
// =============================================================================

export async function getStreams(filters: StreamsFilters = {}): Promise<StreamsResponse> {
  const params: Record<string, string | number> = {
    pageNumber: filters.pageNumber ?? 0,
    pageLength: filters.pageLength ?? 25,
    sortKey: filters.sortKey ?? 'updatedAt',
    sortDirection: filters.sortDirection ?? -1,
  };

  if (filters.search) params.search = filters.search;
  if (filters.organisationName) params.organisationName = filters.organisationName;

  return api.get<StreamsResponse>('/v4/streams', params);
}

// =============================================================================
// Stream CRUD (V4 — PostgreSQL)
// =============================================================================

export async function getStreamForEdit(streamId: string): Promise<StreamEditResponse> {
  return api.get<StreamEditResponse>(`/v4/streams/${streamId}/edit`);
}

export async function getStreamNewOptions(): Promise<StreamNewOptionsResponse> {
  return api.get<StreamNewOptionsResponse>('/v4/streams/new-options');
}

export async function createStream(data: StreamFormData): Promise<V2Stream> {
  const response = await api.post<V2Stream>('/v4/streams', data);
  return response;
}

export async function updateStream(streamId: string, data: Partial<StreamFormData>): Promise<V2Stream> {
  const response = await api.put<V2Stream>(`/v4/streams/${streamId}`, data);
  return response;
}

export async function deleteStream(streamId: string): Promise<void> {
  await api.delete(`/v4/streams/${streamId}`);
}

// =============================================================================
// Customer Organisations (V3 — for searchable-select filter)
// =============================================================================

interface V3OrganisationsResponse {
  organisations: Organisation[];
}

export async function getCustomerOrganisations(): Promise<Organisation[]> {
  const allOrgs: Organisation[] = [];
  let page = 0;
  const pageSize = 100;

  while (true) {
    const response = await api.get<V3OrganisationsResponse>('/v4/legacy/organisations/', {
      pageNumber: page,
      pageLength: pageSize,
      sortKey: 'name',
      sortDirection: 1,
      tags: 'customer',
      enabled: 'true',
    });

    const orgs = response.organisations || [];
    allOrgs.push(...orgs);

    if (orgs.length < pageSize) break;
    page++;
  }

  return allOrgs;
}
